import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createCheckoutSession, completeCheckoutSession } from '../../utils/stripe';
import { useAuth } from '../../contexts/AuthContext';
import DateTimeSelector from './DateTimeSelector';
import './SessionBookingModal.css';

const SessionDetailsForm = ({ provider, onNext, onCancel }) => {
  const { checkEmailVerification } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [emailVerificationStatus, setEmailVerificationStatus] = useState(null);
  const [checkingVerification, setCheckingVerification] = useState(true);
  
  const [bookingData, setBookingData] = useState({
    sessionType: '',
    duration: 60, // minutes
    scheduledDate: '',
    scheduledTime: '',
    notes: '',
    amount: provider.hourlyRate || 75,
  });

  // Filter session types based on provider's skills
  const sessionTypes = provider.skills || [];

  // Check email verification on component mount
  useEffect(() => {
    const checkEmailVerificationStatus = async () => {
      try {
        const result = await checkEmailVerification();
        setEmailVerificationStatus(result);
      } catch (error) {
        console.error('Error checking email verification:', error);
        setEmailVerificationStatus({ isVerified: false });
      } finally {
        setCheckingVerification(false);
      }
    };

    checkEmailVerificationStatus();
  }, [checkEmailVerification]);

  const handleInputChange = (field, value) => {
    setBookingData(prev => {
      const updated = { ...prev, [field]: value };
      
      // Recalculate amount when duration changes
      if (field === 'duration') {
        updated.amount = (provider.hourlyRate || 75) * (value / 60);
      }
      
      return updated;
    });
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    setLoading(true);
    setError(null);

    try {
      // Check email verification first
      if (!emailVerificationStatus?.isVerified) {
        throw new Error('You must verify your email address before booking sessions. Please check your account settings.');
      }

      // Validate form
      if (!bookingData.sessionType || !bookingData.scheduledDate || !bookingData.scheduledTime) {
        throw new Error('Please fill in all required fields');
      }

      // Pass session details to next step
      onNext(bookingData);
    } catch (err) {
      console.error('Validation error:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  // Show loading state while checking verification
  if (checkingVerification) {
    return (
      <div className="verification-loading">
        <div className="loading-spinner">⏳</div>
        <p>Checking email verification status...</p>
      </div>
    );
  }

  // Show verification warning if email is not verified
  if (!emailVerificationStatus?.isVerified) {
    return (
      <div className="email-verification-warning">
        <div className="warning-icon">⚠️</div>
        <h3>Email Verification Required</h3>
        <p>
          You must verify your email address before booking sessions. 
          This helps ensure account security and allows providers to send you important updates.
        </p>
        <div className="verification-actions">
          <button 
            onClick={() => window.open('/account', '_blank')} 
            className="btn-primary"
          >
            Go to Account Settings
          </button>
          <button onClick={onCancel} className="btn-secondary">
            Close
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="booking-form">
      <div className="form-section">
        <h3>Session Details</h3>
        
        <div className="form-group">
          <label>Session Type *</label>
          <select
            value={bookingData.sessionType}
            onChange={(e) => handleInputChange('sessionType', e.target.value)}
            required
          >
            <option value="">Select session type</option>
            {sessionTypes.map(type => (
              <option key={type} value={type}>{type}</option>
            ))}
          </select>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Duration (minutes) *</label>
            <select
              value={bookingData.duration}
              onChange={(e) => handleInputChange('duration', parseInt(e.target.value))}
              required
            >
              <option value={30}>30 minutes</option>
              <option value={45}>45 minutes</option>
              <option value={60}>60 minutes</option>
              <option value={90}>90 minutes</option>
              <option value={120}>120 minutes</option>
            </select>
          </div>

          <div className="form-group">
            <label>Total Amount</label>
            <div className="amount-display">{formatCurrency(bookingData.amount)}</div>
          </div>
        </div>

        <div className="form-group">
          <label>Schedule Session *</label>
          <DateTimeSelector
            provider={provider}
            selectedDate={bookingData.scheduledDate}
            selectedTime={bookingData.scheduledTime}
            duration={bookingData.duration}
            onDateChange={(date) => handleInputChange('scheduledDate', date)}
            onTimeChange={(time) => handleInputChange('scheduledTime', time)}
          />
        </div>

        <div className="form-group">
          <label>Session Details *</label>
          <textarea
            value={bookingData.notes}
            required
            onChange={(e) => handleInputChange('notes', e.target.value)}
            placeholder="Any specific topics or goals for this session?"
            rows={3}
          />
        </div>
      </div>


      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="form-actions">
        <button type="button" onClick={onCancel} className="btn-cancel">
          Cancel
        </button>
        <button
          type="submit"
          disabled={loading}
          className="btn-book"
        >
          {loading ? 'Processing...' : `Continue to Payment - ${formatCurrency(bookingData.amount)}`}
        </button>
      </div>
    </form>
  );
};

const SessionBookingModal = ({ provider, onClose, currentUser, bookingResult: propBookingResult = null, initialStep = 'details' }) => {
  const navigate = useNavigate();
  const [step, setStep] = useState(initialStep);
  const [sessionDetails, setSessionDetails] = useState(null);
  const [bookingResult, setBookingResult] = useState(propBookingResult);
  const [loading, setLoading] = useState(false);

  // Update internal state when prop changes
  useEffect(() => {
    if (propBookingResult) {
      setBookingResult(propBookingResult);
      setStep('success');
    }
  }, [propBookingResult]);

  const handleDetailsNext = async (details) => {
    setSessionDetails(details);
    setLoading(true);
    
    try {
      // Create Stripe Checkout session
      const result = await createCheckoutSession(
        details.amount,
        provider.id,
        `${provider.firstName} ${provider.lastName}`,
        details,
        `${window.location.origin}/provider/${provider.username || provider.id}`,
        `${window.location.origin}/provider/${provider.username || provider.id}`
      );
      
      // Redirect to Stripe Checkout
      window.location.href = result.url;
    } catch (error) {
      console.error('Error creating checkout session:', error);
      alert('Error creating payment session. Please try again.');
      setLoading(false);
    }
  };

  // Remove the handleCheckoutSuccess function as it's now handled in parent

  const handleClose = () => {
    onClose();
  };

  return (
    <div className="booking-modal-overlay">
      <div className="booking-modal">
        <div className="modal-header">
          <h2>
            {step === 'details' && `Book Session with ${provider.firstName} ${provider.lastName}`}
            {step === 'success' && 'Session Booked Successfully!'}
          </h2>
          <button onClick={handleClose} className="close-btn">×</button>
        </div>

        <div className="modal-body">
          {step === 'details' && (
            <SessionDetailsForm
              provider={provider}
              onNext={handleDetailsNext}
              onCancel={handleClose}
            />
          )}

          {loading && (
            <div className="loading-content">
              <div className="loading-icon">⏳</div>
              <h3>Redirecting to secure payment...</h3>
              <p>Please wait while we prepare your Stripe Checkout session.</p>
            </div>
          )}

          {step === 'success' && (
            <div className="success-content">
              <div className="success-icon">✅</div>
              <h3>Your session has been booked!</h3>
              <div className="booking-summary">
                <p><strong>Session Type:</strong> {bookingResult.sessionData.sessionType}</p>
                <p><strong>Duration:</strong> {bookingResult.sessionData.duration} minutes</p>
                <p><strong>Date:</strong> {bookingResult.sessionData.scheduledDate}</p>
                <p><strong>Time:</strong> {bookingResult.sessionData.scheduledTime}</p>
                <p><strong>Amount Paid:</strong> ${bookingResult.sessionData.amount}</p>
              </div>
              <p>The provider will confirm your session shortly. You'll receive a confirmation email once confirmed.</p>
              <div className="success-actions">
                <button 
                  onClick={() => {
                    navigate('/practitioner-sessions');
                    onClose();
                  }} 
                  className="btn-primary"
                >
                  View My Sessions
                </button>
                <button onClick={handleClose} className="btn-secondary">
                  Close
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SessionBookingModal;